import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, Package, Filter, ArrowLeft } from 'lucide-react';

import { API } from '@/config';

export default function StorageMapPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [locations, setLocations] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selectedZone, setSelectedZone] = useState('Freezer-FG');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user came from dashboard
  const fromDashboard = location.state?.fromDashboard;

  const zones = [
    { name: 'Freezer-FG', label: 'Freezer - Finished Goods', capacity: 500 },
    { name: 'Freezer-RM', label: 'Freezer - Raw Material', capacity: 1000 },
    { name: 'Freezer-Pick', label: 'Freezer - Pick Zone', capacity: 300 },
    { name: 'Cooler', label: 'Cooler', capacity: 400 },
    { name: 'Dry', label: 'Dry Storage', capacity: 1000 }
  ];

  useEffect(() => {
    fetchData();
  }, [selectedZone]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [locResponse, invResponse] = await Promise.all([
        axios.get(`${API}/warehouse/locations?zone=${selectedZone}`),
        axios.get(`${API}/warehouse/inventory`)
      ]);
      setLocations(locResponse.data);
      setInventory(invResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const getLocationStatus = (location) => {
    const hasInventory = inventory.some(inv => inv.location_id === location.location_id);
    if (hasInventory) {
      const inv = inventory.find(inv => inv.location_id === location.location_id);
      if (inv.status === 'on_hold') return 'hold';
      if (inv.status === 'allocated') return 'allocated';
      return 'occupied';
    }
    return 'empty';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'empty': return 'bg-gray-300 hover:bg-gray-400';
      case 'occupied': return 'bg-green-500 hover:bg-green-600';
      case 'allocated': return 'bg-purple-500 hover:bg-purple-600';
      case 'hold': return 'bg-red-500 hover:bg-red-600';
      default: return 'bg-gray-300';
    }
  };

  const handleLocationClick = async (location) => {
    try {
      const response = await axios.get(`${API}/warehouse/locations/${location.location_id}`);
      setSelectedLocation(response.data);
    } catch (error) {
      console.error('Error fetching location details:', error);
    }
  };

  // Filter locations based on search
  const filteredLocations = searchTerm
    ? locations.filter(loc => 
        loc.location_code.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : locations;

  // Group locations for grid display
  const groupedLocations = () => {
    if (selectedZone === 'Freezer-Pick') {
      // Floor-level layout for Pick Zone
      return filteredLocations;
    } else {
      // Rack layouts - group by aisle
      const grouped = {};
      filteredLocations.forEach(loc => {
        const aisle = loc.aisle || 'unknown';
        if (!grouped[aisle]) grouped[aisle] = [];
        grouped[aisle].push(loc);
      });
      return grouped;
    }
  };

  const currentZoneInfo = zones.find(z => z.name === selectedZone);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {fromDashboard && (
            <button
              onClick={() => navigate('/warehouse')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <MapPin className="w-8 h-8" />
              Storage Map
            </h1>
            <p className="text-gray-600 mt-1">Visual warehouse layout and inventory locations</p>
          </div>
        </div>
      </div>

      {/* Zone Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold">Select Zone</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {zones.map(zone => (
            <button
              key={zone.name}
              onClick={() => setSelectedZone(zone.name)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedZone === zone.name
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {zone.label}
              <span className="ml-2 text-sm opacity-75">({zone.capacity})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search location code (e.g., FRZ-FG-A01-R01-L05)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map View */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold">{currentZoneInfo?.label}</h2>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-300 rounded"></div>
                <span>Empty</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>Occupied</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-500 rounded"></div>
                <span>Allocated</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>On Hold</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading locations...</div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
              {typeof groupedLocations() === 'object' && !Array.isArray(groupedLocations()) ? (
                // Rack layout
                <div className="space-y-6">
                  {Object.entries(groupedLocations()).map(([aisle, locs]) => (
                    <div key={aisle} className="border rounded-lg p-4">
                      <div className="font-semibold mb-3 text-lg">Aisle {aisle}</div>
                      <div className="grid grid-cols-10 gap-1">
                        {locs.map(loc => {
                          const status = getLocationStatus(loc);
                          return (
                            <button
                              key={loc.location_id}
                              onClick={() => handleLocationClick(loc)}
                              className={`w-8 h-8 rounded ${getStatusColor(status)} cursor-pointer transition-all text-xs text-white font-semibold flex items-center justify-center`}
                              title={loc.location_code}
                            >
                              {loc.level}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Floor layout (Pick Zone, Docks, Holds)
                <div className="grid grid-cols-10 gap-2">
                  {filteredLocations.map(loc => {
                    const status = getLocationStatus(loc);
                    return (
                      <button
                        key={loc.location_id}
                        onClick={() => handleLocationClick(loc)}
                        className={`aspect-square rounded ${getStatusColor(status)} cursor-pointer transition-all p-2 text-xs text-white font-semibold flex items-center justify-center`}
                        title={loc.location_code}
                      >
                        {loc.position || loc.level}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Location Details Panel */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Location Details</h2>
          
          {selectedLocation ? (
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500">Location Code</div>
                <div className="text-lg font-bold">{selectedLocation.location_code}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Zone</div>
                  <div className="font-semibold">{selectedLocation.zone}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Capacity</div>
                  <div className="font-semibold">{selectedLocation.capacity_pallets} pallet(s)</div>
                </div>
                {selectedLocation.aisle && (
                  <div>
                    <div className="text-sm text-gray-500">Aisle</div>
                    <div className="font-semibold">{selectedLocation.aisle}</div>
                  </div>
                )}
                {selectedLocation.rack && (
                  <div>
                    <div className="text-sm text-gray-500">Rack</div>
                    <div className="font-semibold">{selectedLocation.rack}</div>
                  </div>
                )}
                {selectedLocation.level && (
                  <div>
                    <div className="text-sm text-gray-500">Level</div>
                    <div className="font-semibold">{selectedLocation.level}</div>
                  </div>
                )}
                <div>
                  <div className="text-sm text-gray-500">Temperature</div>
                  <div className="font-semibold capitalize">{selectedLocation.temperature_zone || 'N/A'}</div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="text-sm text-gray-500 mb-2">Inventory</div>
                {selectedLocation.inventory && selectedLocation.inventory.length > 0 ? (
                  <div className="space-y-3">
                    {selectedLocation.inventory.map((inv, idx) => (
                      <div key={idx} className="bg-gray-50 p-3 rounded border">
                        <div className="font-semibold">{inv.item_name}</div>
                        <div className="text-sm text-gray-600">
                          LP: {inv.lp_number}
                        </div>
                        <div className="text-sm text-gray-600">
                          Lot: {inv.lot_number}
                        </div>
                        <div className="text-sm text-gray-600">
                          Qty: {inv.quantity} {inv.uom}
                        </div>
                        <div className="mt-1">
                          <span className={`text-xs px-2 py-1 rounded ${
                            inv.status === 'available' ? 'bg-green-100 text-green-700' :
                            inv.status === 'on_hold' ? 'bg-red-100 text-red-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {inv.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 text-center py-4">
                    <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <div>Empty Location</div>
                  </div>
                )}
              </div>

              {selectedLocation.qr_code_image && (
                <div className="border-t pt-4">
                  <div className="text-sm text-gray-500 mb-2">QR Code</div>
                  <img 
                    src={selectedLocation.qr_code_image} 
                    alt="Location QR Code"
                    className="w-32 h-32 mx-auto border"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <div>Click on a location to view details</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
